import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { CampaignCreativeRollout, Creative } from '@/api/types';
import { FailureNotice } from '@/components/failure-notice';
import { ResponsivePicker } from '@/components/responsive-picker';
import { fmtDate } from '@/lib/datetime';

export interface PinnedVersionBadgeProps {
  /** The library creative this slot came from, when it is loaded. */
  creative: Pick<Creative, 'id' | 'revision' | 'approved_revision'> | undefined;
  /** The revision the campaign plan pins for this slot. */
  pinnedRevision: number | undefined;
  /** When the pinned revision was approved, once the revisions query has it. */
  approvedAt?: string | null;
  /** The campaign has left draft: it keeps its pin whatever the creative does. */
  campaignLive: boolean;
  /** The campaign's name, for the confirmation's plain words. */
  campaignName?: string;
  /** Where the campaign delivers, for the same words. */
  provider?: 'meta' | 'ttd';
  /** The newest rollout requested for this slot, if any. */
  rollout?: CampaignCreativeRollout | null;
  /** Requests the rollout; absent when the slot cannot roll out (no campaign id yet, a draft). */
  onRollout?: (toRevision: number) => void;
  /**
   * Why `onRollout` is absent, in the caller's words, so the disabled control
   * names its own reason (the inert-control rule). Falls back to a generic
   * sentence when the caller has none.
   */
  rolloutUnavailableReason?: string;
  /** The request is on its way. */
  rolloutPending?: boolean;
  onVersions?: () => void;
}

/**
 * A campaign slot always names the version it flies. When a newer approved
 * version exists, a draft campaign picks it up on save; a live campaign gets
 * "Roll out version N" — a sibling ad on Meta with the old one paused, a
 * creative swap on the TTD ad group — behind a confirmation that says what it
 * costs delivery. That cost is not the same on both providers and must never
 * be said as if it were: Meta reviews the new ad, so delivery stops for 30–90
 * minutes; The Trade Desk reviews nothing at roll-out, so the ad group serves
 * the new version as soon as the swap lands. The reason for every state is
 * plain words: phones have no hover to hide it in.
 */
export function PinnedVersionBadge({
  creative,
  pinnedRevision,
  approvedAt,
  campaignLive,
  campaignName,
  provider = 'meta',
  rollout,
  onRollout,
  rolloutUnavailableReason,
  rolloutPending = false,
  onVersions,
}: PinnedVersionBadgeProps) {
  const [confirming, setConfirming] = useState(false);
  if (!creative || !pinnedRevision) return null;
  const newer = creative.approved_revision !== null && creative.approved_revision > pinnedRevision;
  const inFlight = rollout && (rollout.status === 'queued' || rollout.status === 'running');
  const failed = rollout && rollout.status === 'failed';
  const versionsLink = onVersions ? (
    <button type="button" className="btn btn-sm" onClick={onVersions}>
      View versions
    </button>
  ) : (
    <Link
      to="/creatives"
      search={{ focus: creative.id, tab: 'versions' }}
      className="pinned-version-link"
    >
      View versions
    </Link>
  );
  const campaign = campaignName ?? 'this campaign';
  const providerName = provider === 'ttd' ? 'The Trade Desk' : 'Meta';
  return (
    <div
      className="pinned-version"
      data-newer={newer ? 'true' : undefined}
      data-lock-exempt={campaignLive ? 'true' : undefined}
    >
      <div className="pinned-version-tags">
        <span className="tag pinned-version-tag">
          v{pinnedRevision}
          {approvedAt ? ` · approved ${fmtDate(approvedAt)}` : ''}
        </span>
        {newer && !inFlight && (
          <span className="tag pinned-version-tag--newer">
            Version {creative.approved_revision} available
          </span>
        )}
        {inFlight && (
          <span className="tag pinned-version-tag--newer" data-rollout-status={rollout.status}>
            Rolling out version {rollout.to_revision}…
          </span>
        )}
      </div>
      {inFlight && provider === 'ttd' && (
        <div className="field-hint pinned-version-hint" role="status">
          {providerName} is swapping the creatives on the ad group. Delivery does not stop: the ad
          group serves version {rollout.to_revision} as soon as the swap lands. {versionsLink}
        </div>
      )}
      {inFlight && provider !== 'ttd' && (
        <div className="field-hint pinned-version-hint" role="status">
          {providerName} is creating the new ad and pausing the old one. Delivery resumes once{' '}
          {providerName} has reviewed it, usually within 30–90 minutes. {versionsLink}
        </div>
      )}
      {failed && !inFlight && (
        <div className="field-hint pinned-version-hint pinned-version-error" role="alert">
          The roll-out to version {rollout.to_revision} did not complete. Some steps at{' '}
          {providerName} may have completed. Nothing runs again on its own: request the roll-out
          again to finish it.
          {rollout.last_error && (
            <FailureNotice dense announce={false} failure={rollout.failure} fallback={rollout.last_error} />
          )}
        </div>
      )}
      {newer && !inFlight && !campaignLive && (
        <div className="field-hint pinned-version-hint">
          Saved with version {creative.approved_revision} when you save the campaign. {versionsLink}
        </div>
      )}
      {newer && !inFlight && campaignLive && (
        <div className="pinned-version-rollout">
          <button
            type="button"
            className="btn"
            disabled={!onRollout || rolloutPending}
            aria-describedby={`rollout-why-${creative.id}`}
            onClick={() => setConfirming(true)}
          >
            Roll out version {creative.approved_revision}
          </button>
          <div id={`rollout-why-${creative.id}`} className="field-hint pinned-version-hint">
            {onRollout
              ? `${campaign} keeps version ${pinnedRevision} until you roll the new one out.`
              : (rolloutUnavailableReason ??
                'Applying a version requires publishing permission and no conflicting creative operation.')}{' '}
            {versionsLink}
          </div>
          {confirming && onRollout && creative.approved_revision !== null && (
            <ResponsivePicker
              kind="confirmation"
              label={`Roll out version ${creative.approved_revision}`}
              onClose={() => setConfirming(false)}
            >
              <div
                className="pinned-version-confirm"
                role="dialog"
                aria-label={`Roll out version ${creative.approved_revision}`}
                data-rollout-confirm
              >
                <p>
                  {provider === 'ttd'
                    ? `${providerName} gets version ${creative.approved_revision}'s creatives on ${campaign}'s ad group and drops version ${pinnedRevision}'s.`
                    : `${providerName} gets a new ad for version ${creative.approved_revision} on ${campaign}, and the version ${pinnedRevision} ad is paused.`}{' '}
                  Nothing else in the campaign changes: same audience, same budget, same dates.
                </p>
                {provider === 'ttd' ? (
                  <p>
                    <strong>Delivery does not stop</strong>: {providerName} reviews nothing at
                    roll-out, so the ad group serves version {creative.approved_revision} as soon as
                    the swap lands and version {pinnedRevision} stops serving at the same moment. A
                    paused campaign stays paused after the swap.
                  </p>
                ) : (
                  <p>
                    <strong>Delivery stops for 30–90 minutes</strong> while {providerName} reviews
                    the new ad, then continues on version {creative.approved_revision}. A paused
                    campaign stays paused after the replacement.
                  </p>
                )}
                <div className="pinned-version-confirm-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setConfirming(false);
                      onRollout(creative.approved_revision as number);
                    }}
                  >
                    Roll out version {creative.approved_revision}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setConfirming(false)}
                  >
                    Not now
                  </button>
                </div>
              </div>
            </ResponsivePicker>
          )}
        </div>
      )}
      {!newer && !inFlight && !failed && (
        <div className="field-hint pinned-version-hint">{versionsLink}</div>
      )}
    </div>
  );
}
